import { Box, Center, Group, Stack, Text } from '@mantine/core';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { ContractRealizationRow } from '@/api/generated/models';
import { CardSection, ListPagination, tableRowStyle } from '@/components/common';
import { PanelInfoIcon } from '@/features/board/components/PanelInfoIcon';
import { currentLocale } from '@/utils/format';
import { useLocalPagination } from '@/utils/useLocalPagination';

const COLUMNS: [string, number][] = [
  ['board.table.colContract', 120],
  ['board.table.colClient', 160],
  ['board.table.colObject', 140],
  ['board.table.colContractedValue', 110],
  ['board.table.colRealizedValue', 110],
  ['board.table.colPlannedHours', 90],
  ['board.table.colRealizedHours', 90],
  ['board.table.colDeviationHours', 100],
  ['board.table.colRealizationPercent', 90],
  ['board.table.colStatus', 120]
];

/** Bez "h" sufiksa — stupci već imaju "(H)" u headeru (vidi Figma), nema potrebe ponavljati na
 * svakom retku/u footeru. */
const formatHours = (value: number) =>
  (value ?? 0).toLocaleString(currentLocale(), { maximumFractionDigits: 2 });

/** Isto bez "EUR" sufiksa — stupci već imaju "(EUR)" u headeru. */
const formatValue = (value: number) =>
  (value ?? 0).toLocaleString(currentLocale(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const RIGHT_ALIGNED_COLUMNS = new Set([
  'board.table.colContractedValue',
  'board.table.colRealizedValue',
  'board.table.colPlannedHours',
  'board.table.colRealizedHours',
  'board.table.colDeviationHours',
  'board.table.colRealizationPercent'
]);

/** flexGrow = izvorna px širina (omjer ostaje isti), flexBasis 0 — stupci razvuku red preko cijele
 * širine panela umjesto da ostave prazninu desno kad je sadržaj uži od dostupnog prostora. */
const colFlex = (width: number) => ({ flex: `${width} 1 0px` });

/**
 * Tablica ugovora s odstupanjem, dijeljena i PM i Management pogledu (Figma BD-01/BD-02) — naslov
 * se doslovno razlikuje po pogledu u Figmi, pa ga svaka stranica prosljeđuje kao prop umjesto
 * zajedničkog i18n ključa. Sortirano po najvećem odstupanju prvo (deviationHours silazno).
 */
export const ContractRealizationTable = ({
  title,
  info,
  rows
}: {
  title: string;
  info?: string;
  rows: ContractRealizationRow[];
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const sorted = useMemo(
    () => [...rows].sort((a, b) => (b.deviationHours ?? 0) - (a.deviationHours ?? 0)),
    [rows]
  );

  // Klijentska paginacija (isti obrazac kao stavke ugovora) — zbroj u footeru je zbroj TRENUTNE
  // stranice, kao i na ostalim listama.
  const { page, setPage, pageSize, setPageSize, totalPages, pageItems } =
    useLocalPagination(sorted);
  // Filter (partner/objekt) može smanjiti broj ugovora ispod trenutne stranice — vrati na zadnju postojeću.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages, setPage]);

  const pagination = (bordered: boolean) =>
    sorted.length > 0 && (
      <ListPagination
        label={t('board.table.showing', { shown: pageItems.length, total: sorted.length })}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        bordered={bordered}
      />
    );

  const totalContractedValue = pageItems.reduce((sum, r) => sum + (r.plannedValue ?? 0), 0);
  const totalRealizedValue = pageItems.reduce((sum, r) => sum + (r.realizedValue ?? 0), 0);
  const totalPlanned = pageItems.reduce((sum, r) => sum + (r.plannedHours ?? 0), 0);
  const totalRealized = pageItems.reduce((sum, r) => sum + (r.realizedHours ?? 0), 0);
  // deviationHours s backenda = planirano - realizirano (pozitivno = zaostaje). Figma prikazuje
  // zaostatak kao NEGATIVAN broj obojan crveno ("−400") — displayDeviation negira samo za prikaz,
  // backend polje ostaje nepromijenjeno (koristi se i za sortiranje).
  const totalDeviation = totalPlanned - totalRealized;
  const totalPercent = totalPlanned > 0 ? (totalRealized / totalPlanned) * 100 : 0;

  return (
    <>
      {/* Mobile (Figma BD-02M) — nema okvira panela oko cijele sekcije, samo naslov + info ikona
          pa lista kartica direktno ispod (svaka kartica ima vlastiti okvir). */}
      <Stack gap={12} hiddenFrom="sm" w="100%">
        <Group justify="space-between" align="center" wrap="nowrap">
          <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
            {title}
          </Text>
          {info && <PanelInfoIcon label={info} title={title} />}
        </Group>
        {sorted.length === 0 ? (
          <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
            {t('board.table.empty')}
          </Text>
        ) : (
          pageItems.map((row) => {
            const isBehindPlan = (row.deviationHours ?? 0) > 0;
            return (
              <Stack
                key={row.contractId}
                gap={8}
                p={16}
                role="button"
                tabIndex={0}
                onClick={() => row.contractId && navigate(`/contracts/${row.contractId}`)}
                onKeyDown={(event) => {
                  if ((event.key === 'Enter' || event.key === ' ') && row.contractId)
                    navigate(`/contracts/${row.contractId}`);
                }}
                style={{
                  borderRadius: 12,
                  border: '1px solid var(--color-border-soft)',
                  backgroundColor: 'var(--color-bg-surface)',
                  cursor: 'pointer'
                }}
              >
                <Group justify="space-between" wrap="nowrap">
                  <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                    {row.contractNumber}
                  </Text>
                  <Group gap={6} wrap="nowrap">
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        flexShrink: 0,
                        backgroundColor: isBehindPlan
                          ? 'var(--color-status-danger)'
                          : 'var(--chart-realization-good)'
                      }}
                    />
                    <Text fz={13} style={{ color: 'var(--color-text-primary)' }}>
                      {isBehindPlan
                        ? t('board.table.belowPlanLabel')
                        : t('board.table.onPlanLabel')}
                    </Text>
                  </Group>
                </Group>
                <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
                  {[row.clientOrgName, row.objectName].filter(Boolean).join(' · ') || '—'}
                </Text>
                <Group gap={10} wrap="nowrap" align="center">
                  <div
                    style={{
                      flex: 1,
                      height: 6,
                      minWidth: 1,
                      borderRadius: 3,
                      backgroundColor: 'var(--color-bg-surface-2)',
                      overflow: 'hidden'
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        borderRadius: 3,
                        width: `${Math.min(Math.max(row.deviationPercent ?? 0, 0), 100)}%`,
                        backgroundColor: 'var(--chart-collected)'
                      }}
                    />
                  </div>
                  <Text fz={11} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                    {(row.deviationPercent ?? 0).toFixed(0)}%
                  </Text>
                </Group>
                <Text fz={10} style={{ color: 'var(--color-text-muted)' }}>
                  {t('board.table.mobileSummary', {
                    planned: formatHours(row.plannedHours ?? 0),
                    realized: formatHours(row.realizedHours ?? 0),
                    deviation: formatHours(-(row.deviationHours ?? 0))
                  })}
                </Text>
                <Text fz={10} style={{ color: 'var(--color-text-muted)' }}>
                  {t('board.table.mobileValues', {
                    contracted: formatValue(row.plannedValue ?? 0),
                    realized: formatValue(row.realizedValue ?? 0)
                  })}
                </Text>
              </Stack>
            );
          })
        )}
        {pagination(false)}
      </Stack>

      {/* Desktop — nepromijenjeno, tablica unutar CardSection panela kao prije. */}
      <Box visibleFrom="sm">
        <CardSection
          title={title}
          headerRight={info ? <PanelInfoIcon label={info} title={title} /> : undefined}
        >
          {sorted.length === 0 ? (
            <Center py={32}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('board.table.empty')}
              </Text>
            </Center>
          ) : (
            <Box style={{ overflowX: 'auto' }}>
              <Box style={{ minWidth: 1040 }}>
                <div
                  className="table-header-row"
                  style={{
                    ...tableRowStyle,
                    padding: '10px 20px',
                    backgroundColor: 'var(--color-bg-canvas)',
                    borderBottom: '1px solid var(--color-border-soft)'
                  }}
                >
                  {COLUMNS.map(([key, width]) => (
                    <Text
                      key={key}
                      fz={10}
                      fw={600}
                      ta={RIGHT_ALIGNED_COLUMNS.has(key) ? 'right' : undefined}
                      style={{
                        color: 'var(--color-text-muted)',
                        letterSpacing: 0.3,
                        marginLeft: key === 'board.table.colStatus' ? 24 : undefined,
                        ...colFlex(width)
                      }}
                    >
                      {t(key)}
                    </Text>
                  ))}
                </div>

                {pageItems.map((row, index) => {
                  const isBehindPlan = (row.deviationHours ?? 0) > 0;
                  const deviationColor = isBehindPlan
                    ? 'var(--color-status-danger)'
                    : 'var(--color-text-primary)';
                  return (
                    <div
                      key={row.contractId}
                      role="button"
                      tabIndex={0}
                      onClick={() => row.contractId && navigate(`/contracts/${row.contractId}`)}
                      onKeyDown={(event) => {
                        if ((event.key === 'Enter' || event.key === ' ') && row.contractId)
                          navigate(`/contracts/${row.contractId}`);
                      }}
                      style={{
                        ...tableRowStyle,
                        padding: '14px 20px',
                        cursor: 'pointer',
                        borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                      }}
                    >
                      <Text
                        fz={13.5}
                        fw={600}
                        style={{ color: 'var(--color-text-primary)', ...colFlex(120) }}
                        truncate
                      >
                        {row.contractNumber}
                      </Text>
                      <Text
                        fz={13}
                        style={{ color: 'var(--color-text-secondary)', ...colFlex(160) }}
                        truncate
                      >
                        {row.clientOrgName ?? '—'}
                      </Text>
                      <Text
                        fz={13}
                        style={{ color: 'var(--color-text-secondary)', ...colFlex(140) }}
                        truncate
                      >
                        {row.objectName ?? '—'}
                      </Text>
                      <Text
                        fz={13}
                        ta="right"
                        style={{ color: 'var(--color-text-secondary)', ...colFlex(110) }}
                      >
                        {formatValue(row.plannedValue ?? 0)}
                      </Text>
                      <Text
                        fz={13}
                        ta="right"
                        style={{ color: 'var(--color-text-secondary)', ...colFlex(110) }}
                      >
                        {formatValue(row.realizedValue ?? 0)}
                      </Text>
                      <Text
                        fz={13}
                        ta="right"
                        style={{ color: 'var(--color-text-secondary)', ...colFlex(90) }}
                      >
                        {formatHours(row.plannedHours ?? 0)}
                      </Text>
                      <Text
                        fz={13}
                        ta="right"
                        style={{ color: 'var(--color-text-secondary)', ...colFlex(90) }}
                      >
                        {formatHours(row.realizedHours ?? 0)}
                      </Text>
                      <Text
                        fz={13}
                        fw={600}
                        ta="right"
                        style={{ color: deviationColor, ...colFlex(100) }}
                      >
                        {formatHours(-(row.deviationHours ?? 0))}
                      </Text>
                      <Text
                        fz={13}
                        ta="right"
                        style={{ color: 'var(--color-text-secondary)', ...colFlex(90) }}
                      >
                        {(row.deviationPercent ?? 0).toFixed(0)}%
                      </Text>
                      <div style={{ ...colFlex(120), display: 'flex', marginLeft: 24 }}>
                        <Group gap={6} wrap="nowrap">
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 999,
                              flexShrink: 0,
                              backgroundColor: isBehindPlan
                                ? 'var(--color-status-danger)'
                                : 'var(--chart-realization-good)'
                            }}
                          />
                          <Text fz={13} style={{ color: 'var(--color-text-primary)' }}>
                            {isBehindPlan
                              ? t('board.table.belowPlanLabel')
                              : t('board.table.onPlanLabel')}
                          </Text>
                        </Group>
                      </div>
                    </div>
                  );
                })}

                <div
                  className="table-header-row"
                  style={{
                    ...tableRowStyle,
                    padding: '12px 20px',
                    backgroundColor: 'var(--color-bg-surface-2)',
                    borderTop: '1px solid var(--color-border-soft)'
                  }}
                >
                  {/* "Ukupno" skroz lijevo (Figma) + prazni placeholderi za Klijent/Objekt — isti
                  colFlex omjeri kao header/redci, da se Plan/Real. ukupno poravnaju TOČNO ispod
                  svojih stupaca. */}
                  <Text
                    fz={13.5}
                    fw={600}
                    style={{ color: 'var(--color-text-primary)', ...colFlex(120) }}
                  >
                    {t('board.table.totalLabel')}
                  </Text>
                  <div style={colFlex(160)} />
                  <div style={colFlex(140)} />
                  <Text
                    fz={14}
                    fw={700}
                    ta="right"
                    style={{ color: 'var(--color-text-primary)', ...colFlex(110) }}
                  >
                    {formatValue(totalContractedValue)}
                  </Text>
                  <Text
                    fz={14}
                    fw={700}
                    ta="right"
                    style={{ color: 'var(--color-text-primary)', ...colFlex(110) }}
                  >
                    {formatValue(totalRealizedValue)}
                  </Text>
                  <Text
                    fz={14}
                    fw={700}
                    ta="right"
                    style={{ color: 'var(--color-text-primary)', ...colFlex(90) }}
                  >
                    {formatHours(totalPlanned)}
                  </Text>
                  <Text
                    fz={14}
                    fw={700}
                    ta="right"
                    style={{ color: 'var(--color-text-primary)', ...colFlex(90) }}
                  >
                    {formatHours(totalRealized)}
                  </Text>
                  <Text
                    fz={14}
                    fw={700}
                    ta="right"
                    style={{
                      color:
                        totalDeviation > 0
                          ? 'var(--color-status-danger)'
                          : 'var(--color-text-primary)',
                      ...colFlex(100)
                    }}
                  >
                    {formatHours(-totalDeviation)}
                  </Text>
                  <Text
                    fz={14}
                    fw={700}
                    ta="right"
                    style={{ color: 'var(--color-text-primary)', ...colFlex(90) }}
                  >
                    {totalPercent.toFixed(0)}%
                  </Text>
                  <div style={{ ...colFlex(120), marginLeft: 24 }} />
                </div>
              </Box>
            </Box>
          )}
          {pagination(true)}
        </CardSection>
      </Box>
    </>
  );
};
